import classNames from "classnames";
import React, { useState, useRef, useEffect } from "react";

const MultiSelectDropdown = ({
  options,
  onSelectionChange,
  defaultSelected = [],
  width = "w-52",
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const parseDefaultSelected = (val) => {
    if (typeof val === "string") {
      return val
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id !== "");
    }
    return Array.isArray(val) ? val : [];
  };

  const [selectedItems, setSelectedItems] = useState(
    parseDefaultSelected(defaultSelected)
  );
  const dropdownRef = useRef<any>(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectedItems(parseDefaultSelected(defaultSelected));
  }, [JSON.stringify(defaultSelected)]);

  const toggleItem = (itemId) => {
    const newSelection = selectedItems.includes(itemId)
      ? selectedItems.filter((id) => id !== itemId)
      : [...selectedItems, itemId];
    setSelectedItems(newSelection);
    onSelectionChange?.(newSelection);
  };

  const toggleAll = () => {
    const allIds = options.map((o) => o.id);
    const newSelection =
      selectedItems.length === options.length ? [] : allIds;
    setSelectedItems(newSelection);
    onSelectionChange?.(newSelection);
  };

  const isAllSelected =
    options.length > 0 && selectedItems.length === options.length;

  return (
    <div className={classNames("relative", width)} ref={dropdownRef}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="
          flex items-center justify-between w-full px-3 py-2 text-sm text-left
          bg-white border border-line rounded-lg
          hover:border-line-strong focus:outline-none focus:border-line-strong focus:shadow-focus
          transition-all duration-150
        "
      >
        <span className={selectedItems.length === 0 ? "text-ink-muted" : "text-ink"}>
          {selectedItems.length === 0
            ? "Select items…"
            : `${selectedItems.length} selected`}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className={classNames("w-4 h-4 text-ink-muted transition-transform duration-150 flex-shrink-0", {
            "rotate-180": isOpen,
          })}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-30 w-full mt-1.5 bg-white border border-line rounded-xl shadow-card-lg overflow-hidden animate-slide-in">
          <ul className="max-h-52 overflow-y-auto">
            {/* Select All */}
            <li className="border-b border-line">
              <button
                type="button"
                onClick={toggleAll}
                className="flex items-center w-full px-3 py-2 gap-2.5 hover:bg-surface transition-colors duration-100"
              >
                <CheckBox checked={isAllSelected} />
                <span className="text-xs font-semibold text-ink-secondary uppercase tracking-wide">
                  Select All
                </span>
              </button>
            </li>

            {options.map((option) => (
              <li key={option.id}>
                <button
                  type="button"
                  onClick={() => toggleItem(option.id)}
                  className="flex items-center w-full px-3 py-2 gap-2.5 hover:bg-surface transition-colors duration-100"
                >
                  <CheckBox checked={selectedItems.includes(option.id)} />
                  <span className="text-sm text-ink">{option.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const CheckBox = ({ checked }: { checked: boolean }) => (
  <div
    className={classNames(
      "w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors duration-150",
      checked
        ? "bg-primary border-primary"
        : "border-line-strong bg-white"
    )}
  >
    {checked && (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2.5}
        stroke="currentColor"
        className="w-2.5 h-2.5 text-white"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
      </svg>
    )}
  </div>
);

export default MultiSelectDropdown;
